/**
 * Re-export from the modular admin API services
 * This file is kept for backward compatibility with existing code
 */
import {
  AdminDashboardStats,
  AdminListing,
  AdminListingFilters,
  StatusChangeRequest,
} from "@/lib/types/adminTypes";
import { ListingInput } from "@/lib/types/listingTypes"; // Assuming ListingInput is needed
import { User } from "@/lib/types/userTypes"; // Assuming User type is needed for response/payloads
import apiClient from "../apiClient";

// Type for the payload of POST /api/admin/listings
interface AdminCreateListingPayload extends ListingInput {
  user_id: number; // Admin specifies the owner
}

// Type for the response of POST /api/admin/listings (adjust based on actual API)
interface AdminCreateListingResponse {
  status: string;
  message?: string;
  data: AdminListing;
}

// Type for GET /api/admin/users response (adjust based on actual API)
interface AdminUsersResponse {
  status: string;
  data: {
    users: User[];
    // Add pagination if the API supports it
  };
}

export const adminService = {
  getAllListings: (filters: AdminListingFilters = {}) => {
    return apiClient.get("/api/admin/listings", {
      params: filters,
      isAdminRequest: true,
    } as any);
  },

  getListingDetails: (id: number | string) => {
    return apiClient.get(`/api/admin/listings/${id}`, {
      isAdminRequest: true,
    } as any);
  },

  // ADDED: Function for POST /api/admin/listings
  createAdminListing: (
    data: AdminCreateListingPayload
  ): Promise<{ data: AdminCreateListingResponse }> => {
    return apiClient.post("/api/admin/listings", data, {
      isAdminRequest: true,
    } as any);
  },

  updateListing: (id: number | string, data: Partial<AdminListing>) => {
    return apiClient.put(`/api/admin/listings/${id}`, data, {
      isAdminRequest: true,
    } as any);
  },

  deleteListing: (id: number | string) => {
    return apiClient.delete(`/api/admin/listings/${id}`, {
      isAdminRequest: true,
    } as any);
  },

  changeListingStatus: (id: number | string, data: StatusChangeRequest) => {
    return apiClient.patch(`/api/admin/listings/${id}/status`, data, {
      isAdminRequest: true,
    } as any);
  },

  getDashboardStats: (): Promise<{ data: { data: AdminDashboardStats } }> => {
    return apiClient.get("/api/admin/dashboard/stats", {
      isAdminRequest: true,
    } as any);
  },

  // ADDED: Function for GET /api/admin/users (assuming endpoint exists)
  getAllUsers: (params?: {
    page?: number;
    limit?: number;
    role?: string;
    search?: string;
  }): Promise<{ data: AdminUsersResponse }> => {
    return apiClient.get("/api/admin/users", {
      params,
      isAdminRequest: true,
    } as any);
  },

  // Bulk operations...
  bulkDeleteListings: (ids: (number | string)[]) => {
    return apiClient.post("/api/admin/listings/bulk-delete", { ids }, {
      isAdminRequest: true,
    } as any);
  },

  bulkUpdateStatus: (
    ids: (number | string)[],
    statusData: StatusChangeRequest
  ) => {
    return apiClient.post(
      "/api/admin/listings/bulk-status",
      { ids, ...statusData },
      { isAdminRequest: true } as any
    );
  },
};

// Default export for backward compatibility
export default adminService;
